import {ReactNode, useEffect, useRef} from 'react';

import {createContext, isDefined} from 'shared/web';

import {useVideoUploadManager} from '../hooks/useVideoUploadManager';
import {GetChunkFn} from '../types/GetChunkFn';
import {HandleMultiMultiPartUploadType} from '../types/HandleMultiMultiPartUploadType';
import {QueueVideoTask} from '../types/QueueVideoTask';
import {UploadApi} from '../types/UploadApi';
import {VideoUploadError} from '../types/VideoUploadError';
import {checkIsTaskExpired} from '../utils/checkIsTaskExpired';

type QueueVideoUploadContext = {
  tasks: QueueVideoTask[];
  /**
   * Retrieves the progress of each task in the video upload queue.
   * This iterates through the list of video upload tasks, calculating the progress of each task based on the
   * status of its jobs. A task's progress is determined by the ratio of completed (`DONE`) jobs to the total number of
   * jobs within that task. The function returns an array of objects, each containing the task's ID and its progress
   * percentage. If a task has no jobs, the function returns `null` for that task to indicate that progress calculation
   * is not applicable.
   *
   * @returns An array of objects, each with a `connectionFileId` string and a `progress` number representing the task's progress.
   *          The `progress` is a decimal value between 0 and 1, where 1 signifies 100% completion.
   *          If a task has no jobs, returns `null` for that task.
   */
  uploadProgress: {
    id: string;
    /**
     * The progress of the task, represented a number between 0 and 100.
     * 100 signifies that the task is complete.
     */
    progress: number;
    connectionFileId: string;
  }[];

  removeTask: (id: string) => void;
  /**
   * Drops every task and cancels the chunk uploads still in flight. Meant for the moment the owner
   * of the queue goes away while work is running (a logout): nothing left behind is going to be
   * attached, so finishing it would only waste bandwidth and raise errors for the next session.
   */
  clearTasks: () => void;
  /**
   * Initiates a multipart upload process for a video file.
   * This function handles the initiation of a multipart upload for a given video file.
   * It retrieves necessary server information for the upload, creates a queue video task from the file,
   * and enqueues it for processing.
   *
   * @param file - The video file to be uploaded.
   * @returns The ID of the video task that has been enqueued for processing.
   *
   * @remarks
   * This function is designed to be called when a video file needs to be uploaded via multipart upload.
   * It initiates the multipart upload process, prepares the necessary metadata, and enqueues the upload task
   * for processing. The actual upload process is handled asynchronously.
   */
  handleMultiMultiPartUpload: HandleMultiMultiPartUploadType;
};

const [Provider, useVideoUploadQueueContext] = createContext<QueueVideoUploadContext>({
  strict: true,
  name: 'VideoUploadQueueContext',
});

interface Props {
  api: UploadApi;
  children: ReactNode;
  getChunk: GetChunkFn;
  handleError: (errorMessage: VideoUploadError, error?: Error) => void;
}

function VideoUploadQueueProvider({children, getChunk, handleError, api}: Props) {
  // Presign ids (`connectionFileId`) of the attempts whose completion call has already been
  // issued. Keyed by attempt, not by `task.id`: a retried video keeps its id across attempts but
  // gets a fresh presign, and a guard keyed by the id would skip the retry forever.
  const completedAttemptIdsRef = useRef(new Set<string>());

  const {
    tasks,
    removeTask,
    clearTasks,
    processTask,
    uploadProgress,
    handleExpiredTask,
    handleMultiMultiPartUpload,
  } = useVideoUploadManager(api, handleError);

  const addAttemptToCompleted = (connectionFileId: string) => {
    completedAttemptIdsRef.current.add(connectionFileId);
  };

  const handleClearTasks = () => {
    completedAttemptIdsRef.current.clear();
    clearTasks();
  };

  /**
   * Automatically processes the next pending upload task in the queue.
   * This effect triggers whenever there is a change in the `tasks` array or the `processTask` function, ensuring
   * that any task marked as 'PENDING' is processed as soon as possible. It is a crucial part of the system that
   * maintains the flow of task processing without manual intervention.
   *
   * @sideEffects
   * - Initiates the processing of an upload task by calling `processTask`.
   * - Can result in updating the state of tasks, potentially triggering re-renders.
   */
  useEffect(
    function processNextPendingTask() {
      const taskToRun = tasks?.find((task) => task.status === 'PENDING');

      if (!isDefined(taskToRun)) {
        return;
      }

      if (checkIsTaskExpired(taskToRun)) {
        handleExpiredTask(taskToRun);
        return;
      }

      // The effect can observe a stale snapshot in which an attempt still reads PENDING with every
      // job DONE after its completion call was already issued; running `processTask` again would
      // complete the same attempt twice. A failed completion removes the task and the retry
      // arrives under a new presign id, so it is not caught by this guard.
      if (!completedAttemptIdsRef.current.has(taskToRun.connectionFileId)) {
        processTask(taskToRun, getChunk, addAttemptToCompleted);
      }
    },
    [getChunk, handleExpiredTask, processTask, tasks]
  );

  return (
    <Provider
      value={{
        tasks,
        uploadProgress,
        handleMultiMultiPartUpload,
        removeTask,
        clearTasks: handleClearTasks,
      }}
    >
      {children}
    </Provider>
  );
}

export {useVideoUploadQueueContext, VideoUploadQueueProvider}; // eslint-disable-line react/only-export-components -- Pre-existing violation
